import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import InlineIcon from 'components/utils/InlineIcon'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import type { MouseEventHandler } from 'react'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import FormatCurrency from 'components/Common/FormatCurrency'

const ClickableIcon = styled(Clickable)`
  // Vertically align the icon with text
  display: flex;
`

interface Props {
  /**
   * @option estimate - when we don't have enough data to calculate the exact points e.g. no dates entered
   * @option exact - when we have enough data to calculate the exact points the customer will earn
   * @option additional - 'earn an additional' amount of points
   * @option nominal - we want to display that the user will earn points without showing the numerical amount
   * @option ratio - we want to display the ratio of points earned per currency unit: e.g. 1.5 points per $1
   */
  calculationType: 'estimate' | 'exact' | 'additional' | 'nominal' | 'ratio'
  earnablePoints: number
  statusCredits?: number
  pointsEarnRatio?: number
  trailingCaption?: string
  /** does not propagate and is default-prevented */
  onInfoIconClick?: MouseEventHandler<HTMLButtonElement>
}

function _PriceRowLoyaltyPointsContent({
  calculationType,
  earnablePoints,
  statusCredits,
  pointsEarnRatio,
  trailingCaption,
  onInfoIconClick,
}: Props) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()

    onInfoIconClick?.(event)
  }, [onInfoIconClick])

  const showPoints = calculationType !== 'nominal'

  return <Group direction="horizontal" wrap="wrap" gap={4}>
    {showPoints && <>
      {calculationType === 'estimate' && <span>Earn from <FormatLoyaltyPoints abbreviated points={earnablePoints} /></span>}
      {calculationType === 'exact' && <span>Earn <FormatLoyaltyPoints abbreviated points={earnablePoints} /></span>}
      {calculationType === 'additional' && <span>Earn an additional <FormatLoyaltyPoints abbreviated points={earnablePoints} /></span>}
      {calculationType === 'ratio' && <span>Earn <FormatLoyaltyPoints abbreviated points={pointsEarnRatio ?? 0} /> per <FormatCurrency value={1} /></span>}
    </>}
    {!showPoints && <span>
      Earn {getLoyaltyProgramDisplayName()} Points
    </span>}
    <Group direction="horizontal" verticalAlign="center" gap={4} as="span" noDisplayWhenEmpty>
      {showPoints && !!statusCredits && <span> + <FormatLoyaltyStatusCredits statusCredits={statusCredits} /></span> }
      {!!trailingCaption && <Caption variant="medium" colour="neutral-three" weight="normal" as="span">
        {trailingCaption}
      </Caption>}
      {onInfoIconClick && <ClickableIcon onClick={handleClick}>
        <InlineIcon Icon={LineInfoCircleIcon} colour="neutral-one" />
      </ClickableIcon>}
    </Group>
  </Group>
}

export default _PriceRowLoyaltyPointsContent
